<template>
  <div
    id="knowledge_base_tab"
    class="flex flex-col gap-6 proxima_nova max-w-4xl mx-auto w-full h-full overflow-y-auto"
  >
    <VoiceAgentSectionHeader
      :title="tabHeaders.knowledgeBase.title"
      :description="tabHeaders.knowledgeBase.description"
    />

    <div
      v-if="items.length === 0 && !selectedItemType"
      id="knowledge_base_empty_state"
    >
      <CustomContainer :showAddButton="false">
        <div
          class="flex flex-col items-center justify-center gap-6 min-h-[320px]"
        >
          <div class="flex flex-col items-center gap-2">
            <ThemeImage
              :icon="imageNames.EMPTY_SCREEN_GREY"
              alt="knowledge_base_icon"
              class="size-16"
            />
            <span class="text-base font-semibold">Knowledge Base</span>
            <span class="text-sm secondary_color text-center">
              Add knowledge base items to your agent
            </span>
          </div>
          <AddItemDropdown
            button-text="Add"
            :menu-items="availableMenuItems"
            :show-description="true"
            @select="handleAddItem"
          />
        </div>
      </CustomContainer>
    </div>

    <div
      v-else-if="selectedItemType"
      id="knowledge_base_form_container"
      class="flex flex-col gap-4"
    >
      <KnowledgeBaseForm
        :config="selectedItemConfig"
        :item-data="editingItem"
        :is-edit="editingIndex !== null"
        :agent-id="formData.agent_id"
        :knowledge-base-id="knowledgeBaseId"
        :is-saving="isLoading"
        @save="handleSaveEdit"
        @delete="handleDeleteFromEdit"
      />
    </div>

    <div
      v-else-if="items.length > 0"
      id="knowledge_base_items_list"
      class="flex flex-col gap-4"
    >
      <VoiceAgentItemCard
        v-for="(item, index) in items"
        :key="`kb_${item.id || index}`"
        :icon="getItemIcon(item.type)"
        icon-alt="knowledge_base_icon"
        :title="item.name"
        :subtitle="getItemSubtitle(item)"
        :date="item.created_at"
        @edit="handleEditItem(index)"
        @delete="handleDeleteItem(index)"
      />

      <div class="flex justify-center">
        <AddItemDropdown
          button-text="Add"
          :menu-items="availableMenuItems"
          :show-description="true"
          @select="handleAddItem"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { message } from "ant-design-vue";
import { isEqual } from "lodash";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomContainer from "@/src/components/CustomContainer.vue";
import VoiceAgentSectionHeader from "@/src/components/voice_agents/VoiceAgentSectionHeader.vue";
import KnowledgeBaseForm from "@/src/components/voice_agents/knowledge_base/KnowledgeBaseForm.vue";
import VoiceAgentItemCard from "@/src/components/voice_agents/VoiceAgentItemCard.vue";
import AddItemDropdown from "@/src/components/voice_agents/AddItemDropdown.vue";
import VoiceAgentService from "@/src/api/VoiceAgentService.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  knowledgeBaseMenuItems,
  knowledgeBaseTypes,
  tabHeaders,
} from "@/src/constants/voiceAgentConstants.js";
import { formatFileSize } from "@/src/utils/utils.js";
import { extractKnowledgeBaseData, buildKnowledgeBaseFormData } from "@/src/utils/voiceAgentUtils.js";

const props = defineProps({
  formData: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["update:formData"]);

const isLoading = ref(false);
const originalItems = ref([]);
const knowledgeBaseId = ref(null);

const selectedItemType = ref(null);
const selectedItemConfig = ref(null);
const editingIndex = ref(null);
const editingItem = ref(null);

const items = computed(() => props.formData.knowledge_base_items || []);

const availableMenuItems = computed(() => {
  const hasTextItem = items.value.some(item => item.type === knowledgeBaseTypes.TEXT.type);
  
  if (hasTextItem) {
    return knowledgeBaseMenuItems.filter(menuItem => menuItem.type !== knowledgeBaseTypes.TEXT.type);
  }
  
  return knowledgeBaseMenuItems;
});

const getItemIcon = computed(() => (type) => {
  const config = Object.values(knowledgeBaseTypes).find(
    (item) => item.type === type
  );
  return config ? config.icon : imageNames.TEXT_EDITOR_GREY;
});

const getItemSubtitle = (item) => {
  try {
    if (item.type === knowledgeBaseTypes.TEXT.type) {
      return item.text?.substring(0, 100) || "";
    } else if (item.type === knowledgeBaseTypes.LINK.type) {
      return item.url || "";
    } else if (item.type === knowledgeBaseTypes.FILE.type) {
      const fileName = item.file?.name || "";
      const fileSize = item.file?.size ? formatFileSize(item.file.size) : "";
      return fileSize ? `${fileName} (${fileSize})` : fileName;
    }
    return "";
  } catch (error) {
    console.error("Error getting item subtitle:", error);
    return "";
  }
};

const closeForm = () => {
  try {
    selectedItemType.value = null;
    selectedItemConfig.value = null;
    editingIndex.value = null;
    editingItem.value = null;
  } catch (error) {
    console.error("Error closing form:", error);
  }
};

const openForm = (config, item = null, index = null) => {
  try {
    selectedItemType.value = config.type;
    selectedItemConfig.value = config;
    editingIndex.value = index;
    editingItem.value = item ? { ...item } : null;
  } catch (error) {
    console.error("Error opening form:", error);
  }
};

const updateItems = (updatedItems) => {
  try {
    emit("update:formData", {
      ...props.formData,
      knowledge_base_items: updatedItems,
    });
  } catch (error) {
    console.error("Error updating items:", error);
  }
};

const handleAddItem = (config) => {
  try {
    openForm(config);
  } catch (error) {
    console.error("Error adding item:", error);
  }
};

const handleEditItem = (index) => {
  try {
    const item = items.value[index];
    const config = Object.values(knowledgeBaseTypes).find(
      (kb) => kb.type === item.type
    );
    openForm(config, item, index);
  } catch (error) {
    console.error("Error editing item:", error);
  }
};

const handleSaveEdit = async (itemData) => {
  if (!props.formData.agent_id) {
    message.error("Agent ID is required");
    return;
  }

  try {
    isLoading.value = true;
    const knowledgeBaseName = props.formData.name || "Knowledge Base";
    const updatedItems = [...items.value];

    if (editingIndex.value !== null) {
      updatedItems[editingIndex.value] = {
        ...itemData,
        id: updatedItems[editingIndex.value]?.id,
        kb_id: updatedItems[editingIndex.value]?.kb_id,
        created_at:
          updatedItems[editingIndex.value]?.created_at ||
          new Date().toISOString(),
      };
    } else {
      updatedItems.push({
        ...itemData,
        created_at: new Date().toISOString(),
      });
    }

    const formData = buildKnowledgeBaseFormData(updatedItems, props.formData.agent_id, knowledgeBaseName);

    if (!knowledgeBaseId.value) {
      const response = await VoiceAgentService.createKnowledgeBase(formData);
      if (response?.id) {
        knowledgeBaseId.value = response.id;
      }
    } else {
      await VoiceAgentService.updateKnowledgeBase(knowledgeBaseId.value, formData);
    }

    await loadKnowledgeBases();
    message.success("Knowledge base item saved successfully");
    closeForm(); 
  } catch (error) {
    console.error("Error saving item:", error);
    message.error("Failed to save knowledge base item");
  } finally {
    isLoading.value = false;
  }
};

const handleDeleteFromEdit = async () => {
  try {
    if (editingIndex.value !== null) {
      await handleDeleteItem(editingIndex.value);
    }
    closeForm();
  } catch (error) {
    console.error("Error deleting from edit:", error);
  }
};

const handleDeleteItem = async (index) => {
  if (!knowledgeBaseId.value) {
    const updatedItems = [...items.value];
    updatedItems.splice(index, 1);
    updateItems(updatedItems);
    return;
  }

  const itemToDelete = items.value[index];
  if (!itemToDelete) {
    message.error("Item not found");
    return;
  }

  try {
    isLoading.value = true;

    const updatedItems = [...items.value];
    updatedItems.splice(index, 1);

    if (updatedItems.length === 0) {
      await VoiceAgentService.deleteKnowledgeBase(knowledgeBaseId.value);
      knowledgeBaseId.value = null;
      updateItems([]);
      message.success("Knowledge base deleted successfully");
    } else {
      const knowledgeBaseName = props.formData.name || "Knowledge Base";
      const formData = buildKnowledgeBaseFormData(updatedItems, props.formData.agent_id, knowledgeBaseName);
      await VoiceAgentService.updateKnowledgeBase(knowledgeBaseId.value, formData);
      await loadKnowledgeBases();
      message.success("Knowledge base item deleted successfully");
    }
  } catch (error) {
    console.error("Error deleting item:", error);
    message.error("Failed to remove item");
  } finally {
    isLoading.value = false;
  }
};

const loadKnowledgeBases = async () => {
  if (!props.formData.agent_id) {
    return;
  }

  try {
    isLoading.value = true;
    const response = await VoiceAgentService.getKnowledgeBasesByAgent(
      props.formData.agent_id
    );

    if (Array.isArray(response)) {
      const tempFormData = { knowledge_base_items: [] };
      const updatedFormData = extractKnowledgeBaseData(response, tempFormData);
      const transformedItems = updatedFormData.knowledge_base_items || [];
      
      if (transformedItems.length > 0 && transformedItems[0].kb_id) {
        knowledgeBaseId.value = transformedItems[0].kb_id;
      } else {
        knowledgeBaseId.value = null;
      }
      
      originalItems.value = JSON.parse(JSON.stringify(transformedItems));
      updateItems(transformedItems);
    }
  } catch (error) {
    console.error("Error loading knowledge bases:", error);
  } finally {
    isLoading.value = false;
  }
};

const hasItemsChanged = () => {
  return !isEqual(items.value, originalItems.value);
};

const handleContinue = async () => {
  if (!props.formData.agent_id) {
    message.error("Agent ID is required");
    return false;
  }

  if (items.value.length === 0 && originalItems.value.length === 0) {
    return true;
  }

  try {
    isLoading.value = true;
    const knowledgeBaseName = props.formData.name || "Knowledge Base";

    if (knowledgeBaseId.value) {
      if (items.value.length === 0) {
        await VoiceAgentService.deleteKnowledgeBase(knowledgeBaseId.value);
        knowledgeBaseId.value = null;
      } else if (hasItemsChanged()) {
        const formData = buildKnowledgeBaseFormData(items.value, props.formData.agent_id, knowledgeBaseName);
        await VoiceAgentService.updateKnowledgeBase(knowledgeBaseId.value, formData);
      }
    } else if (items.value.length > 0) {
      const formData = buildKnowledgeBaseFormData(items.value, props.formData.agent_id, knowledgeBaseName);
      await VoiceAgentService.createKnowledgeBase(formData);
    }

    await loadKnowledgeBases();
    message.success("Knowledge base saved successfully");
    return true;
  } catch (error) {
    console.error("Error in knowledge base tab:", error);
    message.error("Failed to save knowledge base");
    return false;
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  loadKnowledgeBases();
});

defineExpose({
  handleContinue,
});
</script>
